import { useState } from "react";
import { toast } from "sonner";
import { Clock, Plus, Minus, Sparkles, Sofa, ShoppingBag } from "lucide-react";
import { TEXTILE_SERVICES } from "@/data/textileServices";
import { useCart } from "@/contexts/CartContext";
import { usePricing } from "@/contexts/PricingContext";
import { useContent } from "@/contexts/ContentContext";

export default function Textile() {
  const { content } = useContent();
  const items = [
    ...TEXTILE_SERVICES.map((s) => ({ ...s, image: content.images?.[s.id] || s.image })),
    ...(content.custom?.textile || []).map((s) => ({ ...s, custom: true, image: content.images?.[s.id] || s.image })),
  ];
  return (
    <section id="textile" className="relative py-20 lg:py-28 overflow-hidden" data-testid="textile-section">
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-[#E50914]/8 rounded-full blur-[160px] pointer-events-none"></div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Pitch */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-12">
          <Pitch icon={Sofa} title="Shampouineuse pro" desc="Aspiration profonde, retrait des taches et acariens" />
          <Pitch icon={Sparkles} title="Produits doux" desc="Sans agression du tissu, sécurité enfants & animaux" />
          <Pitch icon={Clock} title="Séchage rapide" desc="Quelques heures seulement après notre passage" />
        </div>

        {/* Items grid */}
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">/ 01 — Que voulez-vous nettoyer ?</div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-2">
            Sélectionnez vos articles
          </h2>
          <p className="text-white/50 text-sm mb-8">Choisissez la quantité puis ajoutez chaque article au panier.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((s) => (
              <ItemCard key={s.id} service={s} />
            ))}
          </div>

          <p className="mt-8 text-sm text-white/40 text-center">
            Ajoutez autant d'articles que nécessaire, puis envoyez votre demande depuis le panier via WhatsApp.
          </p>
        </div>
      </div>
    </section>
  );
}

function ItemCard({ service }) {
  const { addItem } = useCart();
  const { pricing } = usePricing();
  const price = pricing.textile?.[service.id] ?? service.price;
  const [qty, setQty] = useState(1);
  const Icon = service.icon || Sofa;

  const handleAdd = () => {
    addItem({
      id: `textile_${service.id}`,
      type: "textile",
      label: service.name,
      price,
      quantity: qty,
    });
    toast.success(`${qty}× ${service.name} ajouté au panier`);
    setQty(1);
  };

  return (
    <div data-testid={`textile-item-${service.id}`}
      className="group relative rounded-xl border bg-gradient-to-b from-[#161616] to-[#0B0B0B] border-white/10 hover:border-[#E50914]/40 transition-all flex flex-col overflow-hidden">
      {/* Image (avec repli sur l'icône si elle ne charge pas) */}
      <div className="relative h-40 bg-white/5 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center z-0">
          <Icon className="w-10 h-10 text-white/15" />
        </div>
        {service.image && (
          <img
            src={service.image}
            alt={service.name}
            loading="lazy"
            className="relative z-10 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/80 to-transparent px-4 py-3">
          <div className="font-black text-white text-sm leading-tight uppercase tracking-wide">{service.name}</div>
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-white/50">{service.desc}</div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-white/10 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/40">Prix unitaire</div>
          <div className="text-lg font-black text-white">{price}€</div>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1}
            data-testid={`textile-minus-${service.id}`}
            className="w-8 h-8 rounded flex items-center justify-center bg-white/5 border border-white/15 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
            <Minus className="w-4 h-4" />
          </button>
          <div className="w-10 text-center font-black text-lg text-white">{qty}</div>
          <button type="button" onClick={() => setQty((q) => q + 1)}
            data-testid={`textile-plus-${service.id}`}
            className="w-8 h-8 rounded flex items-center justify-center bg-[#E50914]/20 border border-[#E50914]/40 hover:bg-[#E50914]/40 transition-all">
            <Plus className="w-4 h-4 text-[#E50914]" />
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={handleAdd}
        data-testid={`textile-add-${service.id}`}
        className="mt-auto w-full inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white font-bold uppercase text-xs tracking-wider px-4 py-3 rounded-lg border border-[#E50914] hover:shadow-[0_0_20px_rgba(229,9,20,0.4)] transition-all"
      >
        <ShoppingBag className="w-4 h-4" /> Ajouter au panier
      </button>
      </div>
    </div>
  );
}

function Pitch({ icon: Icon, title, desc }) {
  return (
    <div className="p-5 rounded-xl bg-[#0A0A0A] border border-white/10 flex items-center gap-3">
      <div className="w-11 h-11 rounded-lg bg-[#E50914]/10 border border-[#E50914]/30 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#E50914]" />
      </div>
      <div>
        <div className="font-bold text-white text-sm">{title}</div>
        <div className="text-xs text-white/55">{desc}</div>
      </div>
    </div>
  );
}
